import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/apiClient";
import { AdminHeader, AdminPage } from "@/components/admin/AdminUI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Trash2, Pencil, Eye, EyeOff, ArrowUp, ArrowDown, Plus } from "lucide-react";

const EMPTY = { name: "", description: "", hidden: false, order: 99 };
function Field({ label, children }) { return <div className="space-y-1.5"><label className="label-eyebrow text-slate-500 block">{label}</label>{children}</div>; }

export default function AreasAdmin() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const load = () => api.get("/categories").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const edit = (c) => { setF({ ...EMPTY, ...c }); setOpen(true); };
  const save = async () => {
    if (!f.name) return toast.error("Name required");
    try {
      if (f.id) await api.patch(`/categories/${f.id}`, f);
      else await api.post("/categories", { ...f, order: items.length });
      toast.success("Saved"); setOpen(false); setF(EMPTY); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const toggleHide = async (c) => { await api.patch(`/categories/${c.id}`, { hidden: !c.hidden }); load(); };
  const del = async (c) => { if (!window.confirm(`Remove "${c.name}"?`)) return; await api.delete(`/categories/${c.id}`); toast.success("Removed"); load(); };
  const move = async (idx, dir) => {
    const arr = [...items]; const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setItems(arr);
    await Promise.all(arr.map((c, i) => api.patch(`/categories/${c.id}`, { order: i })));
  };

  return (
    <>
      <AdminHeader title="Areas of Work" subtitle={`${items.length} areas`} actions={
        <Button onClick={() => { setF(EMPTY); setOpen(true); }} className="bg-npst-navy text-white" data-testid="add-area-btn"><Plus size={15} className="mr-1.5" /> Add Area</Button>
      } />
      <AdminPage>
        <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
          {items.length === 0 ? <p className="p-6 text-slate-400 text-sm">No areas of work yet.</p> : items.map((c, i) => (
            <div key={c.id} className={`flex items-center justify-between px-4 py-3 ${c.hidden ? "opacity-50" : ""}`} data-testid={`area-row-${c.slug}`}>
              <div><span className="font-medium text-npst-ink">{c.name}</span>{c.hidden && <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-400 border border-slate-200 px-1.5 py-0.5 rounded">Hidden</span>}{c.description && <span className="text-slate-400 text-sm ml-2">· {c.description.slice(0, 60)}</span>}</div>
              <div className="flex items-center gap-3 text-slate-400">
                <button onClick={() => move(i, -1)} className="hover:text-npst-navy"><ArrowUp size={15} /></button>
                <button onClick={() => move(i, 1)} className="hover:text-npst-navy"><ArrowDown size={15} /></button>
                <button onClick={() => toggleHide(c)} className="hover:text-npst-navy" data-testid={`toggle-area-${c.slug}`} title={c.hidden ? "Show" : "Hide"}>{c.hidden ? <EyeOff size={15} /> : <Eye size={15} />}</button>
                <button onClick={() => edit(c)} className="hover:text-npst-navy"><Pencil size={15} /></button>
                <button onClick={() => del(c)} className="hover:text-red-600"><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      </AdminPage>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent><DialogHeader><DialogTitle className="font-serif text-2xl">{f.id ? "Edit" : "Add"} Area of Work</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Field label="Name"><Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="area-name" /></Field>
            <Field label="Description"><Textarea rows={2} value={f.description} onChange={(e) => set("description", e.target.value)} /></Field>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.hidden} onChange={(e) => set("hidden", e.target.checked)} /> Hidden from public site</label>
            <Button onClick={save} className="w-full bg-npst-navy text-white" data-testid="area-save-btn">Save Area</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
